import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Button, ConfirmDialog } from "components";
import Provider from "provider";
import { selectGameId, selectGameObject } from "features/gamestate/gameStateSlice";
import { getDisplayStatus, PUBLISH_STATUS } from "../lifecycle";
import logging from "utils/logging";
import "./GameLifecycleActions.css";

const log = logging.getLogger("GameLifecycleActions");

/**
 * Lifecycle transitions that live on the Settings page (spec PRD 2 §6, relocated
 * here per design review): Archive / Restore, and Mark Completed while a game is
 * in progress. Each goes through a two-step ConfirmDialog. Reset lives alongside
 * via the existing ResetGameButton; Delete is in the Danger Zone below.
 */
const COPY = {
  archive: {
    title: "Archive this game?",
    message:
      "Archived games can be restored, but restoring will reset all game state, submissions, and scores. This can't be undone.",
    confirmLabel: "Archive",
    destructive: true,
    run: (id) => Provider.archiveGame(id),
  },
  unarchive: {
    title: "Restore this game?",
    message:
      "Restoring returns the game to your library and resets all game state, submissions, and scores.",
    confirmLabel: "Restore",
    destructive: true,
    run: (id) => Provider.unarchiveGame(id),
  },
  complete: {
    title: "End this game?",
    message:
      "Marks the game as played and files it under Played. Scores and submissions are preserved.",
    confirmLabel: "Mark completed",
    destructive: false,
    run: (id) => Provider.markGamePlayed(id),
  },
};

const GameLifecycleActions = () => {
  const game = useSelector(selectGameObject);
  const gameId = useSelector(selectGameId);
  const [confirm, setConfirm] = useState(null); // { kind, busy }

  if (!game) return null;

  const status = getDisplayStatus(game);
  const isArchived = status === PUBLISH_STATUS.ARCHIVED;
  const isInProgress = status === PUBLISH_STATUS.IN_PROGRESS;
  const c = confirm ? COPY[confirm.kind] : null;

  const doConfirm = async () => {
    if (!confirm) return;
    setConfirm({ ...confirm, busy: true });
    try {
      await COPY[confirm.kind].run(gameId);
    } catch (err) {
      log.error(`Lifecycle ${confirm.kind} failed`, err);
    } finally {
      setConfirm(null);
    }
  };

  return (
    <div className="gx-life">
      {isInProgress && (
        <Button variant="primary" onClick={() => setConfirm({ kind: "complete" })}>
          Mark completed
        </Button>
      )}
      {isArchived ? (
        <Button variant="secondary" onClick={() => setConfirm({ kind: "unarchive" })}>
          Restore game
        </Button>
      ) : (
        <Button variant="secondary" onClick={() => setConfirm({ kind: "archive" })}>
          Archive game
        </Button>
      )}

      <ConfirmDialog
        open={!!confirm}
        onCancel={() => setConfirm(null)}
        onConfirm={doConfirm}
        title={c?.title}
        message={c?.message}
        confirmLabel={c?.confirmLabel}
        destructive={c?.destructive}
        loading={!!confirm?.busy}
      />
    </div>
  );
};

export default GameLifecycleActions;
